import * as React from "react"

/** How long typed characters keep adding to one typeahead query. */
const TYPEAHEAD_RESET_MS = 500

export interface ListboxNavigationItem {
  /** Tells apart options that share a label. */
  value: string
  label: string
  disabled?: boolean
}

export interface UseListboxNavigationOptions {
  items: ListboxNavigationItem[]
  open: boolean
  /** Where the active option starts each time the list opens or changes. */
  initialIndex: number
  onSelect: (index: number) => void
  /**
   * Home, End and typeahead belong to a search field's text when the keys
   * land on one, so they only move the active option on a plain listbox.
   */
  hasTextInput: boolean
}

/**
 * The listbox keyboard model from the WAI-ARIA Authoring Practices, for a list
 * whose focus stays on one element (the listbox or its search field) while
 * `aria-activedescendant` points at the active option.
 *
 * ArrowDown and ArrowUp move between enabled options, Home and End jump to the
 * ends, Enter selects, and on a plain listbox Space also selects and typed
 * characters jump to the next option whose label starts with them. Escape is
 * left to the popover.
 */
export function useListboxNavigation({
  items,
  open,
  initialIndex,
  onSelect,
  hasTextInput,
}: UseListboxNavigationOptions) {
  const [activeIndex, setActiveIndex] = React.useState(initialIndex)
  const typeahead = React.useRef({ query: "", at: 0 })

  // Keyed on the options' content rather than the array, so a parent that
  // rebuilds its options on every render does not throw the active option back
  // each time, while a change of value, order or disabled state still does.
  const itemsKey = items
    .map((item) => `${item.value}\u0001${item.label}\u0001${!!item.disabled}`)
    .join("\u0000")
  React.useEffect(() => {
    setActiveIndex(initialIndex)
    // A query typed before the list closed must not carry into the next one.
    typeahead.current = { query: "", at: 0 }
  }, [initialIndex, itemsKey, open])

  const step = React.useCallback(
    (from: number, direction: 1 | -1) => {
      for (
        let i = from + direction;
        i >= 0 && i < items.length;
        i += direction
      ) {
        if (!items[i].disabled) {
          return i
        }
      }
      return from
    },
    [items],
  )

  const onKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      if (items.length === 0) {
        return
      }
      switch (event.key) {
        case "ArrowDown":
          event.preventDefault()
          setActiveIndex((current) => step(current, 1))
          return
        case "ArrowUp":
          event.preventDefault()
          setActiveIndex((current) => step(current, -1))
          return
        case "Home":
        case "End":
          if (hasTextInput) {
            return
          }
          event.preventDefault()
          setActiveIndex(
            event.key === "Home" ? step(-1, 1) : step(items.length, -1),
          )
          return
        case " ":
          // Space selects on a plain listbox, unless it continues a typeahead
          // query such as "new y".
          if (
            hasTextInput ||
            Date.now() - typeahead.current.at <= TYPEAHEAD_RESET_MS
          ) {
            break
          }
        // falls through
        case "Enter":
          if (activeIndex >= 0 && !items[activeIndex]?.disabled) {
            event.preventDefault()
            onSelect(activeIndex)
          }
          return
      }

      const isCharacter =
        event.key.length === 1 && !event.ctrlKey && !event.metaKey
      if (hasTextInput || !isCharacter || event.altKey) {
        return
      }
      event.preventDefault()
      const now = Date.now()
      const query =
        now - typeahead.current.at > TYPEAHEAD_RESET_MS
          ? event.key.toLowerCase()
          : typeahead.current.query + event.key.toLowerCase()
      typeahead.current = { query, at: now }
      // A single repeated letter cycles through the options it starts, the way
      // a native select does; a longer query keeps the current option if it
      // still matches.
      const isRepeat = [...query].every((char) => char === query[0])
      const start = isRepeat ? activeIndex + 1 : Math.max(activeIndex, 0)
      const needle = isRepeat ? query[0] : query
      for (let offset = 0; offset < items.length; offset++) {
        const i = (start + offset) % items.length
        const item = items[i]
        if (!item.disabled && item.label.toLowerCase().startsWith(needle)) {
          setActiveIndex(i)
          return
        }
      }
    },
    [activeIndex, hasTextInput, items, onSelect, step],
  )

  return { activeIndex, setActiveIndex, onKeyDown }
}
